import { Group, Stack, Text } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, BookOpen, Compass, Crosshair, FlaskConical, PlayCircle, Rocket } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge, Button, Card, Skeleton } from '../ui';
import { useMyLabs } from '../../hooks/useLabs';
import { useMyAssignments } from '../../hooks/useGradebook';
import { useRoadmap, useMyTiers } from '../../hooks/useJourney';
import { useRanges } from '../../hooks/useRanges';

/**
 * "Pick up where you left off" — the single hero CTA on the trainee dashboard.
 *
 * Best-in-class learning platforms (HTB Academy, TryHackMe, PortSwigger) never
 * make the learner decide what to study next; a highlighted "Continue" tile is
 * the strongest completion driver there is (UX research: docs/research/
 * UX-INSTRUCTOR-CURRICULUM.md, Top-10 item #1). This component reads only data
 * the dashboard already loads and picks ONE next action by a priority ladder:
 *
 *   1. An active lab in flight (useMyLabs)          — they are literally mid-work
 *   2. A live range console (ready/attacking)       — jump back into the mission
 *   3. A cohort assignment (overdue → soonest due)  — the next required work
 *   4. The first unlocked recommended item (roadmap)— the recommended next step
 *   5. Awaiting placement                           — set your starting point
 *   6. Nothing yet                                  — browse courses
 *
 * No backend change: every branch resolves to a route the app already has.
 */

type Tone = 'lab' | 'range' | 'assignment' | 'roadmap' | 'placement' | 'browse';

interface NextAction {
  tone: Tone;
  icon: ReactNode;
  kicker: string;
  title: string;
  subtitle: string;
  cta: string;
  badge?: { label: string; variant: 'info' | 'success' | 'warn' | 'danger' | 'neutral' };
  go: () => void;
}

const LAB_OPENABLE = new Set(['ready', 'running', 'attacking', 'completed']);
const LAB_STARTING = new Set(['provisioning', 'pending', 'starting', 'unknown']);
const RANGE_OPENABLE = new Set(['ready', 'attacking', 'completed']);

export function ContinueHero() {
  const navigate = useNavigate();
  const labsQ = useMyLabs();
  const rangesQ = useRanges();
  const assignmentsQ = useMyAssignments();
  const roadmapQ = useRoadmap();
  const tiersQ = useMyTiers();

  // Render nothing jarring while the primary signals load: a slim skeleton
  // keeps the hero slot stable so the page doesn't jump when data arrives.
  const stillLoading =
    labsQ.isPending || rangesQ.isPending || assignmentsQ.isPending || roadmapQ.isPending;
  if (stillLoading) {
    return <Skeleton lines={1} height={104} gap={0} />;
  }

  const action = pickNextAction();
  if (!action) return null;

  return (
    <Card
      variant="elevated"
      style={{
        background:
          'linear-gradient(105deg, rgba(57,217,138,0.14) 0%, rgba(57,217,138,0.04) 42%, rgb(var(--c-card)) 100%)',
        borderColor: 'rgba(57,217,138,0.30)',
      }}
    >
      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Group gap="md" wrap="nowrap" align="center" style={{ flex: 1, minWidth: 260 }}>
          <span
            aria-hidden
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: 14,
              flexShrink: 0,
              background: 'rgba(57,217,138,0.14)',
              border: '1px solid rgba(57,217,138,0.34)',
              color: 'rgb(var(--c-neon))',
            }}
          >
            {action.icon}
          </span>
          <Stack gap={3} style={{ minWidth: 0 }}>
            <Group gap={8} wrap="nowrap">
              <Text size="11px" fw={700} tt="uppercase" c="rgb(var(--c-neon))" style={{ letterSpacing: '0.08em' }}>
                {action.kicker}
              </Text>
              {action.badge ? (
                <Badge variant={action.badge.variant} size="xs">
                  {action.badge.label}
                </Badge>
              ) : null}
            </Group>
            <Text fw={700} size="lg" c="rgb(var(--c-txt-bright))" lineClamp={1}>
              {action.title}
            </Text>
            <Text size="sm" c="dimmed" lineClamp={2}>
              {action.subtitle}
            </Text>
          </Stack>
        </Group>
        <Button variant="primary" onClick={action.go} leftIcon={<ArrowRight size={16} />}>
          {action.cta}
        </Button>
      </Group>
    </Card>
  );

  function pickNextAction(): NextAction | null {
    // 1. An active lab in flight — the strongest resume signal.
    const active = labsQ.data?.active ?? null;
    if (active && (LAB_OPENABLE.has(active.status) || LAB_STARTING.has(active.status))) {
      const starting = LAB_STARTING.has(active.status);
      return {
        tone: 'lab',
        icon: <FlaskConical size={22} />,
        kicker: 'Pick up where you left off',
        title: active.lab_name || active.lab_slug,
        subtitle: starting
          ? 'Your lab environment is still spinning up — open it to watch it come online.'
          : 'You have a lab in progress. Jump back in and keep going.',
        cta: starting ? 'Open lab' : 'Resume lab',
        badge: starting
          ? { label: 'starting', variant: 'warn' }
          : { label: 'in progress', variant: 'success' },
        go: () => navigate({ to: '/labs/$id', params: { id: active.lab_slug } }),
      };
    }

    // 2. A live range console — a deployed mission ready to enter.
    const range = (rangesQ.data ?? []).find((r) => RANGE_OPENABLE.has(r.status));
    if (range) {
      return {
        tone: 'range',
        icon: <Crosshair size={22} />,
        kicker: 'Pick up where you left off',
        title: range.scenarioName,
        subtitle: range.currentPhase
          ? `Your range is live — current phase: ${range.currentPhase}.`
          : 'Your range is live. Open the console and continue the mission.',
        cta: 'Open console',
        badge: { label: 'live', variant: 'success' },
        go: () => navigate({ to: '/trainee/$rangeId', params: { rangeId: range.id } }),
      };
    }

    // 3. A cohort assignment — overdue first, else soonest due, else first.
    const assignments = [...(assignmentsQ.data ?? [])];
    assignments.sort((a, b) => {
      const ta = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
      const tb = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
      return ta - tb;
    });
    const next = assignments[0];
    if (next) {
      const now = Date.now();
      const overdue = next.due_at ? new Date(next.due_at).getTime() < now : false;
      const dueLabel = next.due_at
        ? overdue
          ? 'overdue'
          : `due ${new Date(next.due_at).toLocaleDateString()}`
        : null;
      return {
        tone: 'assignment',
        icon: <PlayCircle size={22} />,
        kicker: 'Next up for your cohort',
        title: next.lab_name,
        subtitle: `${next.cohort_name}${next.required ? ' · required' : ''}${
          dueLabel ? ` · ${dueLabel}` : ''
        }`,
        cta: 'Start lab',
        ...(dueLabel
          ? { badge: { label: dueLabel, variant: overdue ? ('danger' as const) : ('warn' as const) } }
          : {}),
        go: () => navigate({ to: '/labs/$id', params: { id: next.lab_slug } }),
      };
    }

    // 4. The first unlocked recommended item on the tier-ordered roadmap.
    const rec = (roadmapQ.data?.items ?? []).find((i) => i.unlocked);
    if (rec) {
      const label =
        rec.contentType === 'course' ? 'course' : rec.contentType === 'scenario' ? 'scenario' : 'lab';
      const go =
        rec.contentType === 'course'
          ? () => navigate({ to: '/courses/$id', params: { id: rec.contentSlug } })
          : rec.contentType === 'scenario'
            ? () => navigate({ to: '/scenarios/$slug', params: { slug: rec.contentSlug } })
            : () => navigate({ to: '/labs/$id', params: { id: rec.contentSlug } });
      return {
        tone: 'roadmap',
        icon: <Rocket size={22} />,
        kicker: 'Recommended next step',
        title: prettySlug(rec.contentSlug),
        subtitle: `A ${label} on your path${rec.isRequired ? ' · required' : ''}. Start it to keep leveling up.`,
        badge: { label, variant: 'info' },
        cta: 'Start now',
        go,
      };
    }

    // 5. Not placed yet — set the starting point.
    if (tiersQ.data?.journey.state === 'awaiting_placement') {
      return {
        tone: 'placement',
        icon: <Compass size={22} />,
        kicker: 'Start here',
        title: 'Set your starting point',
        subtitle: 'Take the short placement quiz so we can tailor your path to your skills.',
        cta: 'Take placement',
        go: () => navigate({ to: '/journey/placement' }),
      };
    }

    // 6. Nothing assigned or in flight — point at the catalog.
    return {
      tone: 'browse',
      icon: <BookOpen size={22} />,
      kicker: 'Start here',
      title: 'Explore the course catalog',
      subtitle: 'Browse role-based tracks and hands-on labs, and start the one that fits your goal.',
      cta: 'Browse courses',
      go: () => navigate({ to: '/courses' }),
    };
  }
}

/** "windows-memory-forensics" → "Windows Memory Forensics" for a readable title. */
function prettySlug(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
